import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, Euro, Building2, Plus, 
  Search, X, Loader2, CheckCircle, AlertCircle, ExternalLink, Trash2 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI
const ADMIN_EMAIL = 'info@tusciahub.it';
const MAX_DESC_LENGTH = 1000;
const MAX_TITLE_LENGTH = 50;

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('Tutti');
  const jobTypes = ['Tutti', 'Part-Time', 'Full-Time', 'Stage', 'Occasionale'];

  // Modali
  const [isJobOpen, setIsJobOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Stato per il nuovo annuncio
  const [newJob, setNewJob] = useState({
    title: '', company: '', type: 'Part-Time', 
    salary: '', location: '', description: '', 
    contactType: 'Link', 
    contactValue: '',    
    termsAccepted: false
  });

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await checkUser();
      await fetchJobs();
      setLoading(false);
    };
    init();
  }, []);

  // --- FIX SCROLL ---
  useEffect(() => {
    if (isJobOpen || selectedJob) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isJobOpen, selectedJob]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    if (user && user.email === ADMIN_EMAIL) setIsAdmin(true);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getContactLink = (type, value) => {
      if (!value) return '#';
      if (type === 'Email') return `mailto:${value}`;
      if (type === 'Telefono' || type === 'WA') return `https://wa.me/${value.replace(/\s+/g, '')}`; 
      return value.startsWith('http') ? value : `https://${value}`;
  };

  const formatUrl = (url) => {
    if (!url) return '#';
    return url.startsWith('http') || url.startsWith('mailto:') ? url : `https://${url}`;
  };

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase.from('job_items').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setJobs(data || []);
    } catch (error) { console.error(error); }
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    if (!user) return showToast("Devi accedere.", "error");
    if (!newJob.termsAccepted) return showToast("Accetta i termini.", "error");

    const finalLink = getContactLink(newJob.contactType, newJob.contactValue);

    setIsSubmitting(true);
    try {
      // CORREZIONE: Usiamo contact_info invece di link per coerenza col DB
      const { error } = await supabase.from('job_items').insert([{
        title: newJob.title, 
        company: newJob.company, 
        type: newJob.type,
        salary: newJob.salary, 
        location: newJob.location, 
        description: newJob.description,
        contact_info: finalLink,  // <--- SALVA QUI (contact_info)
        user_id: user.id
      }]);

      if (error) throw error;
      
      showToast("Annuncio pubblicato!", "success");
      setIsJobOpen(false);
      setNewJob({ 
          title: '', company: '', type: 'Part-Time', 
          salary: '', location: '', description: '', 
          contactType: 'Link', contactValue: '', 
          termsAccepted: false 
      });
      fetchJobs();
    } catch (error) { showToast("Errore: " + error.message, "error"); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare annuncio?")) return;
    try {
      // Cancellazione sicura (opzionale se hai immagini, qui i job non ne hanno ma lasciamo la logica pulita)
      const { error } = await supabase.from('job_items').delete().eq('id', id);
      if (error) throw error;
      
      setJobs(jobs.filter(j => j.id !== id));
      showToast("Eliminato.", "success");
      if (selectedJob?.id === id) setSelectedJob(null);
    } catch (error) { showToast("Errore.", "error"); }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          job.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'Tutti' || job.type === selectedType;
    return matchesSearch && matchesType;
  });

  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300 outline-none transition-all shadow-sm";

  if (loading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-100 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title="Offerte di Lavoro" description="Trova lavoro a Viterbo." url="/jobs" />
      <div className="absolute top-0 left-[-10%] w-[500px] h-[500px] bg-cyan-200/40 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>

      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[320px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-800' : 'bg-slate-900 text-white border-slate-800'}`}>
            {toast.type === 'error' ? <AlertCircle className="text-red-400"/> : <CheckCircle className="text-green-400"/>}
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader title="Lavoro & Stage" subtitle="Opportunità per studenti a Viterbo." breadcrumbs={[{ label: 'Lavoro' }]} />

        {/* FILTRI */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-10">
          <div className="flex flex-wrap xl:flex-nowrap gap-6 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-6 w-full xl:w-auto flex-grow min-w-0 items-center">
                <div className="relative w-full md:w-96 group shrink-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-500 transition-colors" size={20} />
                <input 
                    type="text" 
                    placeholder="Cerca posizione o azienda..." 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-cyan-200 focus:bg-white transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                </div>
                <div className="flex-1 w-full md:w-auto overflow-x-auto pb-4 custom-scrollbar min-w-0">
                    <div className="flex gap-2">
                    {jobTypes.map(type => (
                        <button 
                        key={type} 
                        onClick={() => setSelectedType(type)}
                        className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition border ${selectedType === type ? 'bg-cyan-600 text-white border-cyan-600 shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:border-cyan-200 hover:text-cyan-600'}`}
                        >
                        {type}
                        </button>
                    ))}
                    </div>
                </div>
            </div>
            <button onClick={() => user ? setIsJobOpen(true) : showToast("Accedi per pubblicare!", "error")} className="w-full xl:w-auto bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-cyan-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0 mt-2 xl:mt-0">
                <Plus size={20}/> Pubblica
            </button>
          </div>
        </div>

        {/* LISTA JOBS */}
        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredJobs.map(job => (
              <div key={job.id} onClick={() => setSelectedJob(job)} className="group bg-gradient-to-b from-white to-cyan-50 rounded-[2.5rem] border-2 border-cyan-100 shadow-xl hover:shadow-2xl hover:border-cyan-300 hover:-translate-y-2 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col h-full relative">
                
                <div className="h-24 bg-white/50 border-b border-cyan-100 relative backdrop-blur-sm">
                    <span className="absolute top-5 right-5 bg-white border border-cyan-100 text-cyan-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide flex items-center gap-1 shadow-sm">
                        <Clock size={12}/> {job.type}
                    </span>
                </div>

                <div className="absolute top-8 left-6 w-20 h-20 bg-white rounded-3xl border-2 border-cyan-100 p-2 shadow-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                    <Building2 size={32} className="text-cyan-300"/>
                </div>

                <div className="pt-8 p-6 flex flex-col flex-grow">
                    <h3 className="font-black text-slate-900 text-xl leading-tight mb-1 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {job.title}
                    </h3>
                    <p className="text-sm font-bold text-slate-500 mb-4 flex items-center gap-1">
                        @ {job.company}
                    </p>

                    <div className="mb-5">
                        <div className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 p-4 rounded-2xl text-white shadow-lg shadow-cyan-200/50 flex flex-col items-center justify-center relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-300">
                            <p className="text-[10px] font-bold opacity-90 uppercase tracking-widest mb-1 text-white/90">Retribuzione</p>
                            <div className="flex items-center gap-2">
                                <Euro size={18} strokeWidth={3} className="text-white opacity-80"/>
                                <span className="text-2xl font-black tracking-tight text-white">{job.salary || 'N/D'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-auto pt-4 border-t border-cyan-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-cyan-600 group-hover:text-cyan-700 transition flex items-center gap-1">
                            <MapPin size={12}/> {job.location || 'Viterbo'}
                        </span>
                        
                        {user && (user.id === job.user_id || isAdmin) && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(job.id); }} className="bg-white text-red-500 p-2.5 rounded-xl hover:bg-red-50 transition z-20 border border-red-100 shadow-sm">
                                <Trash2 size={16}/>
                            </button>
                        )}
                    </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-white border-2 border-dashed border-slate-200 rounded-[3rem]">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400"><Briefcase size={32}/></div>
            <h3 className="font-bold text-slate-900 text-lg">Nessuna offerta trovata</h3>
            <p className="text-slate-400 text-sm mb-6">Prova a cambiare i filtri di ricerca.</p>
          </div>
        )}

        {/* --- MODALE DETTAGLIO --- */}
        {selectedJob && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedJob(null)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-2xl rounded-[2.5rem] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                    
                    <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50 shrink-0">
                        <div className="flex items-center gap-4 pr-2">
                            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-100 flex items-center justify-center shadow-sm shrink-0">
                                <Building2 size={32} className="text-cyan-300"/>
                            </div>
                            <div>
                                <span className="bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide mb-1 inline-block">{selectedJob.type}</span>
                                <h2 className="text-xl md:text-2xl font-black text-slate-900 leading-tight">{selectedJob.title}</h2>
                                <p className="text-sm font-bold text-slate-500">{selectedJob.company}</p>
                            </div>
                        </div>
                        <button onClick={() => setSelectedJob(null)} className="p-2 bg-white rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition shadow-sm border border-slate-100 shrink-0"><X size={20}/></button>
                    </div>

                    <div className="p-6 overflow-y-auto custom-scrollbar flex-1 pb-24 md:pb-8">
                        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-8 rounded-3xl text-white mb-8 shadow-xl shadow-cyan-200 text-center relative overflow-hidden">
                            <div className="relative z-10">
                                <p className="text-xs font-bold opacity-90 uppercase tracking-widest mb-2">Retribuzione</p>
                                <p className="text-4xl font-black">{selectedJob.salary || 'Da concordare'}</p>
                                <div className="mt-4 inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full backdrop-blur-md">
                                    <MapPin size={14}/> <span className="text-xs font-bold">{selectedJob.location}</span>
                                </div>
                            </div>
                        </div>

                        <div className="prose prose-slate prose-sm text-slate-600 mb-8 leading-relaxed">
                            <h3 className="font-bold text-slate-900 text-lg mb-2">Descrizione</h3>
                            <p className="whitespace-pre-wrap">{selectedJob.description}</p>
                        </div>
                        
                        {/* LINK DINAMICO: Contatta l'azienda/Candidati */}
                        {selectedJob.contact_info && (
                            <a href={formatUrl(selectedJob.contact_info)} target="_blank" rel="noopener noreferrer" className="w-full bg-slate-900 text-white py-4 rounded-2xl text-base font-bold flex items-center justify-center gap-2 transition shadow-lg hover:bg-cyan-600 active:scale-95">
                                <ExternalLink size={20}/> Candidati / Contatta
                            </a>
                        )}
                    </div>
                </div>
            </div>
        )}

        {/* --- MODALE UPLOAD --- */}
        {isJobOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsJobOpen(false)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-lg rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 border border-white/20 pb-24 md:pb-8">
                    
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0">
                        <h3 className="text-xl font-black text-slate-900">Pubblica Annuncio</h3>
                        <button onClick={() => setIsJobOpen(false)} className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>
                    </div>

                    <div className="p-6 overflow-y-auto custom-scrollbar">
                        <p className="text-slate-500 text-sm mb-6 font-medium">Compila i dettagli per trovare il candidato ideale.</p>
                        
                        <form onSubmit={handleAddJob} className="space-y-4">
                            <div className="space-y-1">
                                <input type="text" required maxLength={MAX_TITLE_LENGTH} className={inputStyle} placeholder="Titolo Posizione (es. Cameriere)" value={newJob.title} onChange={e => setNewJob({...newJob, title: e.target.value})} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <input type="text" required className={inputStyle} placeholder="Nome Azienda" value={newJob.company} onChange={e => setNewJob({...newJob, company: e.target.value})} />
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="w-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer" value={newJob.type} onChange={e => setNewJob({...newJob, type: e.target.value})}>
                                        {jobTypes.filter(t => t !== 'Tutti').map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <input type="text" className={inputStyle} placeholder="Retribuzione (es. 8€/h)" value={newJob.salary} onChange={e => setNewJob({...newJob, salary: e.target.value})} />
                                <input type="text" className={inputStyle} placeholder="Luogo (es. Viterbo Centro)" value={newJob.location} onChange={e => setNewJob({...newJob, location: e.target.value})} />
                            </div>

                            <textarea className={`${inputStyle} h-32 resize-none`} placeholder="Descrizione del lavoro..." maxLength={MAX_DESC_LENGTH} required value={newJob.description} onChange={e => setNewJob({...newJob, description: e.target.value})}></textarea>
                            
                            {/* CONTATTO FLESSIBILE (LINK/EMAIL/TEL) */}
                            <div className="flex gap-2">
                                <div className="w-1/3 bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shrink-0">
                                    <select 
                                        className="w-full h-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer text-center" 
                                        value={newJob.contactType} 
                                        onChange={e => setNewJob({...newJob, contactType: e.target.value})}
                                    >
                                        <option value="Link">Link</option>
                                        <option value="Email">Email</option>
                                        <option value="Telefono">Tel</option>
                                        <option value="WA">WA</option>
                                    </select>
                                </div>
                                <input 
                                    type="text" 
                                    className={inputStyle} 
                                    placeholder={newJob.contactType === 'Link' ? 'https://...' : newJob.contactType === 'Email' ? 'esempio@email.com' : '+39...'} 
                                    value={newJob.contactValue} 
                                    onChange={e => setNewJob({...newJob, contactValue: e.target.value})} 
                                />
                            </div>
                            
                            <label className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
                                <input type="checkbox" required className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500 cursor-pointer" checked={newJob.termsAccepted} onChange={e => setNewJob({...newJob, termsAccepted: e.target.checked})}/>
                                <span className="text-xs text-slate-500 font-medium">Accetto i <Link to="/terms" className="text-cyan-600 font-bold hover:underline" target="_blank">Termini</Link>.</span>
                            </label>
                            
                            <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-cyan-600 transition shadow-xl active:scale-95 flex items-center justify-center gap-2">
                                {isSubmitting ? <Loader2 className="animate-spin"/> : 'Pubblica Annuncio'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Jobs;